import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import html2canvas from 'html2canvas';
import { Button } from '@/components/ui/button';
import { Feather, Loader2, ArrowRight, Heart, Download } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SharedStory = () => {
  const { token } = useParams();
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reactions, setReactions] = useState(0);
  const [hasReacted, setHasReacted] = useState(false);
  const [reacting, setReacting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const articleRef = useRef(null);

  const handleDownloadPostcard = async () => {
    if (!articleRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(articleRef.current, {
        backgroundColor: '#fef3c7',
        scale: 2,
        useCORS: true,
        logging: false,
      });
      const link = document.createElement('a');
      const safeName = (story?.title || 'story').replace(/[^a-z0-9]+/gi, '_').toLowerCase();
      link.download = `${safeName}_postcard.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      console.error('Postcard export failed:', err);
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    axios
      .get(`${API}/public/stories/${token}`)
      .then(({ data }) => {
        setStory(data);
        setReactions(data.reactions || 0);
        // Check if this browser has already reacted to this story
        setHasReacted(!!localStorage.getItem(`reacted:${token}`));
      })
      .catch((err) => {
        setError(err.response?.status === 404 ? 'Story not found or no longer shared.' : 'Could not load story.');
      })
      .finally(() => setLoading(false));
  }, [token]);

  const handleReact = async () => {
    if (hasReacted || reacting) return;
    setReacting(true);
    try {
      const { data } = await axios.post(`${API}/public/stories/${token}/react`);
      setReactions(data.reactions);
      setHasReacted(true);
      localStorage.setItem(`reacted:${token}`, '1');
    } catch (err) {
      console.warn('Reaction failed', err);
    } finally {
      setReacting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50" data-testid="shared-story-loading">
        <Loader2 className="h-8 w-8 animate-spin text-amber-800" />
      </div>
    );
  }

  if (error || !story) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 p-4" data-testid="shared-story-error">
        <div className="text-center max-w-md">
          <Feather className="h-12 w-12 mx-auto text-amber-400 mb-4" />
          <h1 className="font-display text-2xl font-bold text-amber-950 mb-2">Story not found</h1>
          <p className="text-amber-800 mb-6">{error || 'This story may have been unshared by its author.'}</p>
          <Link to="/">
            <Button className="bg-amber-900 hover:bg-amber-800 text-amber-50">Go to Flash Fiction</Button>
          </Link>
        </div>
      </div>
    );
  }

  const dateFormatted = new Date(story.created_at).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50" data-testid="shared-story-page">
      {/* Header */}
      <header className="border-b border-amber-200/50 bg-white/50 backdrop-blur-sm">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-2" data-testid="shared-story-brand-link">
            <div className="bg-amber-100 p-2 rounded-full">
              <Feather className="h-5 w-5 text-amber-800" />
            </div>
            <span className="font-display text-lg font-bold text-amber-950">Flash Fiction</span>
          </Link>
          <Link to="/register">
            <Button size="sm" className="bg-amber-900 hover:bg-amber-800 text-amber-50" data-testid="shared-story-signup-cta">
              Write your own
              <ArrowRight className="ml-1 h-3 w-3" />
            </Button>
          </Link>
        </div>
      </header>

      {/* Story */}
      <article ref={articleRef} className="container mx-auto px-6 py-12 max-w-3xl">
        {story.category && (
          <div className="mb-4">
            <span className="inline-block bg-amber-100 text-amber-800 text-xs uppercase tracking-wider px-3 py-1 rounded-full capitalize" data-testid="shared-story-category">
              {story.category}
            </span>
          </div>
        )}
        <h1 className="font-display text-4xl md:text-5xl font-bold text-amber-950 mb-3 leading-tight" data-testid="shared-story-title">
          {story.title}
        </h1>
        <p className="text-amber-800/70 mb-8" data-testid="shared-story-meta">
          By <span className="font-medium">{story.author_name}</span> &middot; {dateFormatted} &middot; {story.word_count} words
        </p>

        {story.illustration && (
          <img
            src={story.illustration}
            alt="Story illustration"
            className="w-full rounded-2xl ring-1 ring-amber-200 shadow-md mb-8"
            data-testid="shared-story-illustration"
          />
        )}

        {story.prompt && (
          <div className="bg-white/60 backdrop-blur-sm border border-amber-200 rounded-xl p-5 mb-8">
            <p className="text-xs uppercase tracking-wider text-amber-700 font-medium mb-1">Written from the prompt</p>
            <p className="font-serif-italic text-amber-900 text-lg" data-testid="shared-story-prompt">&ldquo;{story.prompt}&rdquo;</p>
          </div>
        )}

        <div className="prose prose-lg max-w-none">
          <p className="font-serif text-lg text-gray-800 leading-relaxed whitespace-pre-wrap" data-testid="shared-story-content">
            {story.content}
          </p>
        </div>

        {/* Heart reactions */}
        <div className="mt-12 flex flex-col items-center" data-testid="shared-story-reactions">
          <button
            type="button"
            onClick={handleReact}
            disabled={hasReacted || reacting}
            className={`group flex items-center space-x-3 rounded-full px-6 py-3 border-2 transition-all ${
              hasReacted
                ? 'bg-rose-100 border-rose-300 cursor-default'
                : 'bg-white/70 border-amber-200 hover:border-rose-300 hover:bg-rose-50 shadow-sm hover:shadow-md active:scale-95'
            }`}
            data-testid="react-heart-button"
          >
            <Heart
              className={`h-6 w-6 transition-colors ${
                hasReacted ? 'fill-rose-500 text-rose-500' : 'text-amber-700 group-hover:text-rose-500'
              }`}
            />
            <span className={`font-medium ${hasReacted ? 'text-rose-700' : 'text-amber-900'}`} data-testid="react-count">
              {reactions === 0
                ? hasReacted
                  ? 'Thanks!'
                  : 'Tap the heart if this moved you'
                : `${reactions} ${reactions === 1 ? 'heart' : 'hearts'}`}
            </span>
          </button>
          {hasReacted && reactions > 0 && (
            <p className="text-xs text-amber-800 mt-2">You already sent a heart&nbsp;&mdash; the author will feel it &hearts;</p>
          )}
        </div>

        <div className="mt-16 pt-8 border-t border-amber-200/50 text-center">
          <p className="text-amber-800/70 mb-4">Inspired? Craft your own flash fiction.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
            <Link to="/register">
              <Button className="bg-amber-900 hover:bg-amber-800 text-amber-50" data-testid="shared-story-footer-cta">
                Start Writing
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Button
              variant="outline"
              onClick={handleDownloadPostcard}
              disabled={downloading}
              className="border-amber-300 text-amber-800 hover:bg-amber-100"
              data-testid="shared-story-download-button"
            >
              {downloading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Preparing&hellip;
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Save as Postcard
                </>
              )}
            </Button>
          </div>
        </div>
      </article>
    </div>
  );
};

export default SharedStory;
